import { DateRange } from "react-day-picker";
import { useQuery } from "@tanstack/react-query";

import { dates } from "@/lib/dates";
import { guacHttpClient } from "@/lib/http";
import { misc } from "@/lib/misc";
import {
  DateFilterOption,
  HttpResponse,
  PlacementQueryParams,
  PlacementTrackerResponse,
} from "@/types";

const prepareDateRangeQueryParams = (
  queryParams: URLSearchParams,
  dateRange?: DateRange,
) => {
  if (dateRange?.from) {
    queryParams.append("dateAcceptedStart", String(dateRange.from));
  }
  if (dateRange?.to) {
    // End date must be set to the following day to include the entire day
    // We handle this logic here so that the date range on the date range picker can be displayed correctly.
    // More info in dates.handleDateRangeFilter().
    const endDate = new Date(dateRange.to);
    endDate.setDate(endDate.getDate() + 1);
    queryParams.append("dateAcceptedEnd", String(endDate));
  }
};

export function useGetPlacementTracker(queryOptions: PlacementQueryParams) {
  return useQuery({
    queryKey: ["placements", "tracker", queryOptions],
    queryFn: async () => {
      const { dateAccepted, dateAcceptedFilter, ...rest } = queryOptions;
      let queryParams: URLSearchParams;

      // If not using a custom date range, ensure the dates used to query reflect the selected date range as of right now
      if (dateAcceptedFilter === DateFilterOption.Custom) {
        queryParams = misc.prepareQueryParams(rest);
        prepareDateRangeQueryParams(queryParams, dateAccepted);
      } else {
        const dateRange = dates.handleDateRangeFilter(dateAcceptedFilter);
        queryParams = misc.prepareQueryParams(rest);
        prepareDateRangeQueryParams(queryParams, dateRange);
      }

      const path = `placements/tracker?${queryParams.toString()}`;
      const res = await guacHttpClient.get<
        PlacementTrackerResponse[],
        HttpResponse<PlacementTrackerResponse[]>
      >(path);
      return res.data;
    },
    // Keep previous data while fetching new results so the page doesn't unmount.
    placeholderData: (previousData) => previousData,
  });
}
